import { sanitize } from 'dompurify';
import { Grid, Card, CardContent, CardActions, Skeleton, Divider, useMediaQuery, useTheme } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import NumberFormat from 'react-number-format';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroCheckbox,
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroIcon,
	CerebroIconButton,
	CerebroLabel,
	CerebroLink,
	CerebroTextButton,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroStyledBox,
	CerebroText,
} from '../shared/components';
import { ReactComponent as Celebratory } from '../shared/icons/Celebratory.svg';
import { useApi, useAuth } from '../shared/context';
import { useNavigateToNextPage } from '../shared/hooks';
import {
	QueryKeys,
	useCurrentRatesQuery,
	useDealRequestAllocationQuery,
	useMatchingQuery,
} from '../shared/hooks/Queries';
import { DealRequestAllocation, LoanOption } from '../shared/models';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { getApiResponseData } from '../shared/utils/axios';
import { CerebroPhoneNumber, CerebroPhoneNumberHref } from '../shared/constants';
import { CerebroApiEndpoints } from '../shared/utils/api';
import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSliders } from '@fortawesome/free-solid-svg-icons';
import { GetLoanOptionType, GetTypicalCreditSpreadTypeDescription } from '../shared/utils/enum';
import { LoanOptionOverrideForm } from './LoanOptionOverrideForm';

export const DealTypePreference: React.FC = () => {
	const api = useApi();
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const navigate = useNavigateToNextPage();
	const [selectedOnly, setSelectedOnly] = React.useState<boolean>(
		window.localStorage.getItem('selected-only') == 'true'
	);
	useEffect(() => {
		window.localStorage.setItem('selected-only', selectedOnly ? 'true' : 'false');
	}, [selectedOnly]);
	const [editOption, setEditOption] = React.useState<LoanOption | null>(null);
	const closeEditOptionForm = () => {
		setEditOption(null);
	};

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

	const matchingQuery = useMatchingQuery(guid);
	const allocationQuery = useDealRequestAllocationQuery(guid);
	const [selectedOption, setSelectedOption] = React.useState<LoanOption | null>(null);
	const ratesQuery = useCurrentRatesQuery();

	const preferenceMutation = useMutation(
		async ({ values }: DealTypePreferenceMutationVariables) => {
			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.LoanOptions(guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);
				client.invalidateQueries(QueryKeys.Matching.Get(guid));
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });

				navigate();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	return (
		<CerebroPageContainer maxWidth={'90%'}>
			<CerebroBreadcrumb>Loan Options</CerebroBreadcrumb>

			{matchingQuery.isLoading || matchingQuery.isLoading ? (
				<Skeleton height={400} />
			) : matchingQuery.data?.loanOptions?.length == 0 ? (
				<CerebroStyledBox>
					<CerebroText sx={{ marginBottom: 2 }}>
						Your application seems incomplete. Loan options are based on the information you enter in your
						application.
					</CerebroText>
					<CerebroText variant="small">
						Stuck? Give us a call at{' '}
						<CerebroLink href={CerebroPhoneNumberHref}>{CerebroPhoneNumber}</CerebroLink> and we can help
						you.
					</CerebroText>
				</CerebroStyledBox>
			) : (
				<>
					<CerebroHeading size="h1" component="h1" sx={{ marginBottom: 3 }}>
						<CerebroIcon sx={{ paddingRight: 1 }}>
							<Celebratory width="32" height="32" />
						</CerebroIcon>
						Congratulations!
					</CerebroHeading>
					<CerebroText sx={{ marginBottom: 3 }}>
						Based on the information provided, you have been matched to the following loan options. Select
						your preferred loan option(s). You may select more than one.
					</CerebroText>
					<Formik
						initialValues={{
							cashFlowBankPreference: allocationQuery.data?.cashFlowBankPreference ?? false,
							cashFlowNonBankPreference: allocationQuery.data?.cashFlowNonBankPreference ?? false,
							juniorCashFlowNonBankPreference:
								allocationQuery.data?.juniorCashFlowNonBankPreference ?? false,
							assetBasedArInvBankPreference: allocationQuery.data?.assetBasedArInvBankPreference ?? false,
							assetBasedArInvNonBankPreference:
								allocationQuery.data?.assetBasedArInvNonBankPreference ?? false,
							assetBasedFixedAssetsBankPreference:
								allocationQuery.data?.assetBasedFixedAssetsBankPreference ?? false,
							assetBasedFixedAssetsNonBankPreference:
								allocationQuery.data?.assetBasedFixedAssetsNonBankPreference ?? false,
							smallGovernmentPreference: allocationQuery.data?.smallGovernmentPreference ?? false,
							largeGovernmentPreference: allocationQuery.data?.largeGovernmentPreference ?? false,
							ventureDebtPreference: allocationQuery.data?.ventureDebtPreference ?? false,
							poFinancingPreference: allocationQuery.data?.poFinancingPreference ?? false,
							factoringPreference: allocationQuery.data?.factoringPreference ?? false,
							ventureBankNonBankPreference: allocationQuery.data?.ventureBankNonBankPreference ?? false,
							unknownPreference: allocationQuery.data?.unknownPreference ?? false,
							inventoryOnlyPreference: allocationQuery.data?.inventoryOnlyPreference ?? false,
							lenderFinanceBankPreference: allocationQuery.data?.lenderFinanceBankPreference ?? false,
							lenderFinanceNonBankPreference:
								allocationQuery.data?.lenderFinanceNonBankPreference ?? false,
							floorplanFinanceBankPreference:
								allocationQuery.data?.floorplanFinanceBankPreference ?? false,
							floorplanFinanceNonBankPreference:
								allocationQuery.data?.floorplanFinanceNonBankPreference ?? false,

							cashFlowBankPreferenceSelected:
								allocationQuery.data?.cashFlowBankPreferenceSelected ?? false,
							cashFlowNonBankPreferenceSelected:
								allocationQuery.data?.cashFlowNonBankPreferenceSelected ?? false,
							juniorCashFlowNonBankPreferenceSelected:
								allocationQuery.data?.juniorCashFlowNonBankPreferenceSelected ?? false,
							assetBasedArInvBankPreferenceSelected:
								allocationQuery.data?.assetBasedArInvBankPreferenceSelected ?? false,
							assetBasedArInvNonBankPreferenceSelected:
								allocationQuery.data?.assetBasedArInvNonBankPreferenceSelected ?? false,
							assetBasedFixedAssetsBankPreferenceSelected:
								allocationQuery.data?.assetBasedFixedAssetsBankPreferenceSelected ?? false,
							assetBasedFixedAssetsNonBankPreferenceSelected:
								allocationQuery.data?.assetBasedFixedAssetsNonBankPreferenceSelected ?? false,
							smallGovernmentPreferenceSelected:
								allocationQuery.data?.smallGovernmentPreferenceSelected ?? false,
							largeGovernmentPreferenceSelected:
								allocationQuery.data?.largeGovernmentPreferenceSelected ?? false,
							ventureDebtPreferenceSelected: allocationQuery.data?.ventureDebtPreferenceSelected ?? false,
							poFinancingPreferenceSelected: allocationQuery.data?.poFinancingPreferenceSelected ?? false,
							factoringPreferenceSelected: allocationQuery.data?.factoringPreferenceSelected ?? false,
							ventureBankNonBankPreferenceSelected:
								allocationQuery.data?.ventureBankNonBankPreferenceSelected ?? false,
							inventoryOnlyPreferenceSelected:
								allocationQuery.data?.inventoryOnlyPreferenceSelected ?? false,
							lenderFinanceBankPreferenceSelected:
								allocationQuery.data?.lenderFinanceBankPreferenceSelected ?? false,
							lenderFinanceNonBankPreferenceSelected:
								allocationQuery.data?.lenderFinanceNonBankPreferenceSelected ?? false,
							floorplanFinanceBankPreferenceSelected:
								allocationQuery.data?.floorplanFinanceBankPreferenceSelected ?? false,
							floorplanFinanceNonBankPreferenceSelected:
								allocationQuery.data?.floorplanFinanceNonBankPreferenceSelected ?? false,
							bridgeLoanNonBankPreference: allocationQuery.data?.bridgeLoanNonBankPreference ?? false,
							bridgeLoanNonBankPreferenceSelected:
								allocationQuery.data?.bridgeLoanNonBankPreferenceSelected ?? false,
						}}
						onSubmit={FormikUtils.MutateOnSubmit(preferenceMutation.mutate)}
					>
						{({ isSubmitting, handleSubmit, values }) => {
							const isEmployee = currentUser.isEmployeeUser;
							const isBorrowerSelection =
								values.cashFlowBankPreference ||
								values.cashFlowNonBankPreference ||
								values.juniorCashFlowNonBankPreference ||
								values.assetBasedArInvBankPreference ||
								values.assetBasedArInvNonBankPreference ||
								values.assetBasedFixedAssetsBankPreference ||
								values.assetBasedFixedAssetsNonBankPreference ||
								values.smallGovernmentPreference ||
								values.largeGovernmentPreference ||
								values.ventureDebtPreference ||
								values.poFinancingPreference ||
								values.factoringPreference ||
								values.ventureBankNonBankPreference ||
								values.bridgeLoanNonBankPreference;
							const isPreferenceSelected =
								values.cashFlowBankPreferenceSelected ||
								values.cashFlowNonBankPreferenceSelected ||
								values.juniorCashFlowNonBankPreferenceSelected ||
								values.assetBasedArInvBankPreferenceSelected ||
								values.assetBasedArInvNonBankPreferenceSelected ||
								values.assetBasedFixedAssetsBankPreferenceSelected ||
								values.assetBasedFixedAssetsNonBankPreferenceSelected ||
								values.smallGovernmentPreferenceSelected ||
								values.largeGovernmentPreferenceSelected ||
								values.ventureDebtPreferenceSelected ||
								values.poFinancingPreferenceSelected ||
								values.factoringPreferenceSelected ||
								values.ventureBankNonBankPreferenceSelected ||
								values.bridgeLoanNonBankPreferenceSelected;
							const employeeSelectedOnly = isEmployee && selectedOnly;

							return (
								<CerebroForm onSubmit={handleSubmit}>
									{matchingQuery.data?.loanOptions
										?.filter(row => row.isMatched)
										.sort(comparePreferences)
										.map(row => {
											const name = GetLoanOptionType(row.loanOptionType);
											const propertyName = getPropertyName(name);
											const selectedPropertyName = getSelectedPropertyName(name);

											if (isEmployee && employeeSelectedOnly && !row.isSelected) {
												return <React.Fragment key={name}></React.Fragment>;
											}

											if (!isEmployee && isPreferenceSelected && !row.isSelected) {
												return <React.Fragment key={name}></React.Fragment>;
											}

											return (
												<Card
													key={name}
													sx={theme => ({
														marginBottom: 2,
														boxShadow:
															values[propertyName] || values[selectedPropertyName]
																? theme.solidShadowAll(theme.palette.primary.main, 2)
																: undefined,
													})}
													elevation={3}
												>
													<CardContent>
														<Grid
															container
															justifyContent="space-between"
															flexDirection={{ xs: 'column', sm: 'row' }}
														>
															<Grid item>
																<CerebroHeading size="h2" component="h3" gutterBottom>
																	{name}
																</CerebroHeading>
															</Grid>
															<Grid item>
																<CerebroText weight="medium" gutterBottom>
																	{!isEmployee ? (
																		<strong>
																			Matched Lenders: {row.matchedLenders}{' '}
																		</strong>
																	) : (
																		<>
																			{row.isOverridden && (
																				<FontAwesomeIcon
																					icon={faSliders}
																					size="xs"
																					title="Count/capacity have been overridden"
																				/>
																			)}
																			<CerebroTextButton
																				onClick={() => setEditOption(row)}
																			>
																				Matched Lenders: {row.matchedLenders}
																			</CerebroTextButton>
																		</>
																	)}
																</CerebroText>
															</Grid>
														</Grid>

														<Divider sx={{ marginBottom: { xs: 2, sm: 1 } }} />

														<Grid container spacing={2} columns={18} sx={{ marginTop: 2 }}>
															<Grid item xs={1}></Grid>
															<Grid
																item
																xs={18}
																lg={4}
																sx={{
																	borderRight: isMobile ? '' : 'solid 2px #FFCF00',
																}}
															>
																<CerebroText variant="cardContentHeading" gutterBottom>
																	<strong>Loan Capacity</strong>
																</CerebroText>
																<CerebroText gutterBottom>
																	Up to{' '}
																	{NumberUtils.FormatNumber(
																		row.highLoanAmount,
																		'abbreviation'
																	)}
																</CerebroText>
															</Grid>
															<Grid
																item
																xs={18}
																lg={4}
																sx={{
																	borderRight: isMobile ? '' : 'solid 2px #FFCF00',
																}}
															>
																<CerebroText variant="cardContentHeading" gutterBottom>
																	<strong>Interest Rate Spread</strong>
																</CerebroText>
																<CerebroText gutterBottom>
																	{row.typicalCreditSpreadType && (
																		<>
																			{GetTypicalCreditSpreadTypeDescription(
																				row.typicalCreditSpreadType
																			)}
																			{row.typicalCreditSpreadType == 'Fixed' && (
																				<span>, </span>
																			)}
																			{row.typicalCreditSpreadType != 'Fixed' && (
																				<span> + </span>
																			)}
																		</>
																	)}
																	<NumberFormat
																		value={row.lowestRate}
																		displayType="text"
																		suffix="%"
																	/>{' '}
																	to{' '}
																	<NumberFormat
																		value={row.highestRate}
																		displayType="text"
																		suffix="%"
																	/>{' '}
																	{row.rateDetails}
																</CerebroText>
															</Grid>
															<Grid
																item
																xs={18}
																lg={4}
																sx={{
																	borderRight: isMobile ? '' : 'solid 2px #FFCF00',
																}}
															>
																<CerebroText variant="cardContentHeading" gutterBottom>
																	<strong>Amortization</strong>
																</CerebroText>
																<CerebroText gutterBottom>
																	{row.amortization}
																</CerebroText>
															</Grid>
															<Grid item xs={18} lg={4}>
																<CerebroText variant="cardContentHeading" gutterBottom>
																	<strong>Collateral</strong>
																</CerebroText>
																<CerebroText gutterBottom>{row.collateral}</CerebroText>
															</Grid>
														</Grid>

														<Grid container columns={18} sx={{ marginTop: 2 }}>
															<Grid item xs={1}></Grid>
															<Grid item xs={18} lg={4}>
																<CerebroText variant="small" sx={{ paddingTop: '6px' }}>
																	<strong>Loan Structure:</strong>
																</CerebroText>
															</Grid>
															<Grid item xs={18} lg={13}>
																<CerebroText variant="small">
																	{row.loanStructure}
																	<CerebroIconButton
																		color="info"
																		onClick={() => setSelectedOption(row)}
																		title="See description details"
																	>
																		<FontAwesomeIcon
																			icon={faInfoCircle}
																			size="xs"
																		/>
																	</CerebroIconButton>
																</CerebroText>
															</Grid>
															{row.isLimitedByCollateral && (
																<Grid item>
																	<CerebroText
																		sx={{ marginBottom: { xs: 2, sm: 0.5 } }}
																	>
																		A strong personal guarantee may increase
																		available capacity
																	</CerebroText>
																</Grid>
															)}
														</Grid>
													</CardContent>
													<CardActions
														sx={theme => ({
															backgroundColor: theme.palette.gray.background,
															borderTop: `1px solid ${theme.palette.divider}`,
														})}
													>
														<CerebroLabel
															htmlFor={propertyName}
															sx={{ display: 'flex', alignItems: 'center' }}
														>
															<Field
																as={CerebroCheckbox}
																id={propertyName}
																name={propertyName}
																disabled={isSubmitting}
															/>

															<CerebroText variant="hint" component="span">
																{values[propertyName]
																	? 'Preferred'
																	: 'Select Preference'}
															</CerebroText>
														</CerebroLabel>
														{isEmployee && (
															<>
																<CerebroLabel
																	htmlFor={selectedPropertyName}
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		paddingRight: 1,
																	}}
																>
																	<Field
																		as={CerebroCheckbox}
																		id={selectedPropertyName}
																		name={selectedPropertyName}
																		disabled={isSubmitting}
																	/>

																	<CerebroText variant="hint" component="span">
																		{values[selectedPropertyName]
																			? 'Selected'
																			: 'Mark as Selected'}
																	</CerebroText>
																</CerebroLabel>
															</>
														)}
													</CardActions>
												</Card>
											);
										})}

									{!isBorrowerSelection && !isPreferenceSelected && (
										<Card
											sx={theme => ({
												marginBottom: 2,
												boxShadow: values.unknownPreference
													? theme.solidShadowAll(theme.palette.primary.main, 2)
													: undefined,
											})}
											elevation={3}
										>
											<CardContent>
												<CerebroText>
													I am undecided or do not see any loan options that I prefer.
												</CerebroText>
											</CardContent>
											<CardActions>
												<CerebroLabel
													htmlFor="unknownPreference"
													sx={{ display: 'flex', alignItems: 'center' }}
												>
													<Field
														as={CerebroCheckbox}
														id="unknownPreference"
														name="unknownPreference"
														disabled={isSubmitting}
													/>

													<CerebroText variant="hint" component="span">
														{values.unknownPreference ? 'Selected' : 'Select'}
													</CerebroText>
												</CerebroLabel>
											</CardActions>
										</Card>
									)}

									<Grid
										container
										sx={{ paddingBottom: 2, marginTop: 2 }}
										justifyContent={{ xs: 'center', md: 'flex-start' }}
									>
										<Grid item xs={6}>
											<CerebroPrimaryButton loading={isSubmitting} type="submit">
												Save &amp; Continue
											</CerebroPrimaryButton>
										</Grid>
										{isEmployee && (
											<Grid item xs={6} sx={{ textAlign: 'right' }}>
												<CerebroSecondaryButton onClick={() => setSelectedOnly(!selectedOnly)}>
													{selectedOnly ? 'Show All' : 'Show Selected'}
												</CerebroSecondaryButton>
											</Grid>
										)}
									</Grid>
								</CerebroForm>
							);
						}}
					</Formik>
				</>
			)}

			<CerebroDrawer open={selectedOption != null} onClose={() => setSelectedOption(null)}>
				<CerebroHeading>{GetLoanOptionType(selectedOption?.loanOptionType)}</CerebroHeading>
				<CerebroText sx={{ marginTop: 3 }}>
					<div
						id="emailPreview"
						dangerouslySetInnerHTML={{ __html: sanitize(selectedOption?.loanDescription ?? '<p></p>') }}
					></div>
				</CerebroText>

				<CerebroHeading>Current Rates</CerebroHeading>
				{ratesQuery.isLoading && (
					<>
						<Skeleton height={60} />
						<Skeleton height={60} />
					</>
				)}
				{ratesQuery.isSuccess && (
					<>
						<CerebroText sx={{ fontWeight: 'bold', marginTop: 3 }}>SOFR</CerebroText>
						<CerebroText>
							The Secured Overnight Financing Rate (SOFR) is a benchmark interest rate for loans that is
							replacing the London Interbank Offered Rate (LIBOR). Most commercial banks use SOFR as the
							basis for their lending.
						</CerebroText>
						<CerebroText sx={{ marginTop: 1 }}>
							The current SOFR rate is <strong>{ratesQuery.data.sofrRate}%</strong>
						</CerebroText>

						<CerebroText sx={{ fontWeight: 'bold', marginTop: 3 }}>Prime</CerebroText>
						<CerebroText>
							The prime rate is the interest rate that banks charge their most creditworthy corporate
							customers. It is published by the Wall Street Journal, which surveys large banks and
							publishes the consensus prime rate. The prime rate is used by many non-bank lenders as the
							basis for their lending.
						</CerebroText>
						<CerebroText sx={{ marginTop: 1 }}>
							The current Prime rate is <strong>{ratesQuery.data.primeBaseRate}%</strong>
						</CerebroText>
					</>
				)}
			</CerebroDrawer>

			<LoanOptionOverrideForm option={editOption} close={closeEditOptionForm} />
		</CerebroPageContainer>
	);
};

function comparePreferences(first: LoanOption, second: LoanOption): number {
	if (first.highLoanAmount > second.highLoanAmount) {
		return -1;
	}

	if (second.highLoanAmount > first.highLoanAmount) {
		return 1;
	}

	return 0;
}

function getPropertyName(name: string): keyof DealTypePreferenceValues {
	switch (name) {
		case 'Cash Flow Bank':
			return 'cashFlowBankPreference';
		case 'Senior Cash Non-Bank Lending':
			return 'cashFlowNonBankPreference';
		case 'Junior Cash Non-Bank Lending':
			return 'juniorCashFlowNonBankPreference';
		case 'Asset Based Commercial Bank AR/Inv':
			return 'assetBasedArInvBankPreference';
		case 'Asset Based Non-Bank Lending AR/Inv':
			return 'assetBasedArInvNonBankPreference';
		case 'Asset Based Commercial Bank Fixed Assets':
			return 'assetBasedFixedAssetsBankPreference';
		case 'Asset Based Non-Bank Lending Fixed Assets':
			return 'assetBasedFixedAssetsNonBankPreference';
		case 'Small Government Backed Loans':
			return 'smallGovernmentPreference';
		case 'Rural Government Backed Loans':
			return 'largeGovernmentPreference';
		case 'Purchase Order Financing':
			return 'poFinancingPreference';
		case 'Factoring':
			return 'factoringPreference';
		case 'Venture & Recurring Revenue Loans Bank':
			return 'ventureDebtPreference';
		case 'Asset Based Non-Bank Lending Inventory Only':
			return 'inventoryOnlyPreference';
		case 'Lender Finance - Bank':
			return 'lenderFinanceBankPreference';
		case 'Lender Finance - Non-Bank':
			return 'lenderFinanceNonBankPreference';
		case 'Floorplan Finance - Bank':
			return 'floorplanFinanceBankPreference';
		case 'Floorplan Finance - Non-Bank':
			return 'floorplanFinanceNonBankPreference';
		case 'Bridge Loan':
			return 'bridgeLoanNonBankPreference';
		default:
			return 'ventureBankNonBankPreference';
	}
}

function getSelectedPropertyName(name: string): keyof DealTypePreferenceValues {
	switch (name) {
		case 'Cash Flow Bank':
			return 'cashFlowBankPreferenceSelected';
		case 'Senior Cash Non-Bank Lending':
			return 'cashFlowNonBankPreferenceSelected';
		case 'Junior Cash Non-Bank Lending':
			return 'juniorCashFlowNonBankPreferenceSelected';
		case 'Asset Based Commercial Bank AR/Inv':
			return 'assetBasedArInvBankPreferenceSelected';
		case 'Asset Based Non-Bank Lending AR/Inv':
			return 'assetBasedArInvNonBankPreferenceSelected';
		case 'Asset Based Commercial Bank Fixed Assets':
			return 'assetBasedFixedAssetsBankPreferenceSelected';
		case 'Asset Based Non-Bank Lending Fixed Assets':
			return 'assetBasedFixedAssetsNonBankPreferenceSelected';
		case 'Small Government Backed Loans':
			return 'smallGovernmentPreferenceSelected';
		case 'Rural Government Backed Loans':
			return 'largeGovernmentPreferenceSelected';
		case 'Purchase Order Financing':
			return 'poFinancingPreferenceSelected';
		case 'Factoring':
			return 'factoringPreferenceSelected';
		case 'Venture & Recurring Revenue Loans Bank':
			return 'ventureDebtPreferenceSelected';
		case 'Asset Based Non-Bank Lending Inventory Only':
			return 'inventoryOnlyPreferenceSelected';
		case 'Lender Finance - Bank':
			return 'lenderFinanceBankPreferenceSelected';
		case 'Lender Finance - Non-Bank':
			return 'lenderFinanceNonBankPreferenceSelected';
		case 'Floorplan Finance - Bank':
			return 'floorplanFinanceBankPreferenceSelected';
		case 'Floorplan Finance - Non-Bank':
			return 'floorplanFinanceNonBankPreferenceSelected';
		case 'Bridge Loan':
			return 'bridgeLoanNonBankPreferenceSelected';
		default:
			return 'ventureBankNonBankPreferenceSelected';
	}
}

type DealTypePreferenceMutationVariables = FormikUtils.MutateOnSubmitVariables<DealTypePreferenceValues>;
interface DealTypePreferenceValues {
	cashFlowBankPreference: boolean;
	cashFlowNonBankPreference: boolean;
	juniorCashFlowNonBankPreference: boolean;
	assetBasedArInvBankPreference: boolean;
	assetBasedArInvNonBankPreference: boolean;
	assetBasedFixedAssetsBankPreference: boolean;
	assetBasedFixedAssetsNonBankPreference: boolean;
	smallGovernmentPreference: boolean;
	largeGovernmentPreference: boolean;
	ventureDebtPreference: boolean;
	poFinancingPreference: boolean;
	factoringPreference: boolean;
	ventureBankNonBankPreference: boolean;
	unknownPreference: boolean;
	inventoryOnlyPreference: boolean;
	lenderFinanceBankPreference: boolean;
	lenderFinanceNonBankPreference: boolean;
	floorplanFinanceBankPreference: boolean;
	floorplanFinanceNonBankPreference: boolean;

	cashFlowBankPreferenceSelected: boolean;
	cashFlowNonBankPreferenceSelected: boolean;
	juniorCashFlowNonBankPreferenceSelected: boolean;
	assetBasedArInvBankPreferenceSelected: boolean;
	assetBasedArInvNonBankPreferenceSelected: boolean;
	assetBasedFixedAssetsBankPreferenceSelected: boolean;
	assetBasedFixedAssetsNonBankPreferenceSelected: boolean;
	smallGovernmentPreferenceSelected: boolean;
	largeGovernmentPreferenceSelected: boolean;
	ventureDebtPreferenceSelected: boolean;
	poFinancingPreferenceSelected: boolean;
	factoringPreferenceSelected: boolean;
	ventureBankNonBankPreferenceSelected: boolean;
	inventoryOnlyPreferenceSelected: boolean;
	lenderFinanceBankPreferenceSelected: boolean;
	lenderFinanceNonBankPreferenceSelected: boolean;
	floorplanFinanceBankPreferenceSelected: boolean;
	floorplanFinanceNonBankPreferenceSelected: boolean;
	bridgeLoanNonBankPreference: boolean;
	bridgeLoanNonBankPreferenceSelected: boolean;
}
